import React, { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { API_ORIGIN } from '@config/api';
import { useAuth } from './AuthContext';

interface RealtimeContextType {
  socket: Socket | null;
  connected: boolean;
  subscribeToAppointments: (callback: (appointment: any) => void) => () => void;
  subscribeToTasks: (callback: (task: any) => void) => () => void;
  subscribeToMessages: (callback: (message: any) => void) => () => void;
  subscribeToLabResults: (callback: (result: any) => void) => () => void;
  subscribeToAlerts: (callback: (alert: any) => void) => () => void;
}

const RealtimeContext = createContext<RealtimeContextType>({
  socket: null,
  connected: false,
  subscribeToAppointments: () => () => {},
  subscribeToTasks: () => () => {},
  subscribeToMessages: () => () => {},
  subscribeToLabResults: () => () => {},
  subscribeToAlerts: () => () => {}
});

export const useRealtime = () => useContext(RealtimeContext);

export const RealtimeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, token } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!user || !token || (user.role !== 'doctor' && user.role !== 'veterinarian')) {
      return;
    }

    const newSocket = io(API_ORIGIN, {
      auth: {
        token
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5
    });

    newSocket.on('connect', () => {
      console.log('✅ WebSocket connected');
      setConnected(true);
    });

    newSocket.on('disconnect', () => {
      console.log('❌ WebSocket disconnected');
      setConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [user, token]);

  const subscribeToAppointments = (callback: (appointment: any) => void) => {
    if (!socket) return () => {};

    socket.on('appointment:updated', callback);

    return () => {
      socket.off('appointment:updated', callback);
    };
  };

  const subscribeToTasks = (callback: (task: any) => void) => {
    if (!socket) return () => {};

    socket.on('task:updated', callback);

    return () => {
      socket.off('task:updated', callback);
    };
  };

  const subscribeToMessages = (callback: (message: any) => void) => {
    if (!socket) return () => {};

    socket.on('message:new', callback);

    return () => {
      socket.off('message:new', callback);
    };
  };

  const subscribeToLabResults = (callback: (result: any) => void) => {
    if (!socket) return () => {};

    socket.on('lab:result', callback);

    return () => {
      socket.off('lab:result', callback);
    };
  };

  const subscribeToAlerts = (callback: (alert: any) => void) => {
    if (!socket) return () => {};

    socket.on('alert:clinical', callback);

    return () => {
      socket.off('alert:clinical', callback);
    };
  };

  return (
    <RealtimeContext.Provider
      value={{
        socket,
        connected,
        subscribeToAppointments,
        subscribeToTasks,
        subscribeToMessages,
        subscribeToLabResults,
        subscribeToAlerts
      }}
    >
      {children}
    </RealtimeContext.Provider>
  );
};

